import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";
import TextInput from "@scripts/react/components/TextInput";

const MAX_NAME_LENGTH = 200;
const HELPER_TEXT = `Up to ${MAX_NAME_LENGTH} characters.`;

const validateTrimmedName = (trimmed: string): string | null => {
    if (trimmed.length > MAX_NAME_LENGTH) {
        return `Must be ${MAX_NAME_LENGTH} characters or fewer.`;
    }
    return null;
};

interface WorkflowNameModalProps {
    isOpen: boolean;
    defaultName: string;
    isSubmitting: boolean;
    onClose: () => void;
    onSubmit: (name: string) => void;
}

export const WorkflowNameModal = ({
    isOpen,
    defaultName,
    isSubmitting,
    onClose,
    onSubmit
}: WorkflowNameModalProps) => {
    const [name, setName] = useState(defaultName);
    const prevIsOpenRef = useRef(isOpen);

    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            setName(defaultName);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, defaultName]);

    const trimmedName = name.trim();
    const validationError = validateTrimmedName(trimmedName);
    const canSubmit = trimmedName.length > 0 && !validationError && !isSubmitting;

    const handleSubmit = () => {
        if (!canSubmit) return;
        onSubmit(trimmedName);
    };

    return (
        <ModalWithIcon
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={handleSubmit}
            onCancel={onClose}
            type="info"
            icon="far fa-rocket"
            title="Name your model"
            size="md"
            overlayClassName="bg-opacity-50"
            submitButtonText={isSubmitting ? "Creating..." : "Use Model"}
            cancelButtonText="Cancel"
            submitButtonProps={{ disabled: !canSubmit, loading: isSubmitting }}
        >
            <div className="flex flex-col gap-1">
                <TextInput
                    label="Model name:"
                    value={name}
                    onChange={setName}
                    placeholder={defaultName}
                    autoFocus
                    fullWidth
                />
                <div
                    className={classNames(
                        "text-xs",
                        validationError ? "text-red-500" : "text-gray-500"
                    )}
                >
                    {validationError ?? HELPER_TEXT}
                </div>
            </div>
        </ModalWithIcon>
    );
};
